import axios from 'axios';
import config from '../../config';
import { provisionalJwt } from '../cookies';

// The startup requests for one token: the profile, the organizations when the
// token already carries the "user" claim, and the organization named in the
// URL. Plain axios on fixed v1 paths, on purpose: these run before the
// OpenAPI client has its definition and, for a returning visitor, with the
// previous visit's token, which must never reach the shared client (its
// interceptors report auth errors app-wide and would end the session).

export function decodeClaims(token) {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}

// GET on the v1 API with a given token; resolves with the response body.
// Failures reject in the shape the shared client's interceptor produces
// ({ statusCode, code, message }, the API's own message when it sent one),
// so the login pages show the same text and the store can tell a revoked
// session apart.
export function apiGet(token, path, orgId = null) {
  return axios.get(path, {
    baseURL: config.VUE_APP_NUCLEUS_API,
    headers: { Authorization: token, ...(orgId && { OrganizationId: orgId }) },
  }).then((response) => response.data, (error) => {
    throw {
      statusCode: error?.response?.status,
      code: error?.response?.data?.errorCode || (error?.response?.status === 401 ? 'ERR_UNAUTHORIZED' : ''),
      message: error?.response?.data?.message || error?.message,
    };
  });
}

// `orgId` is the org the router will land on: the one in the URL, or the
// persisted one it falls back to when the URL has none (the caller knows).
export function bootstrap(token, orgId = null) {
  const claims = decodeClaims(token);
  const me = apiGet(token, '/api/v1/users/me', orgId);
  const orgs = claims.user ? apiGet(token, '/api/v1/users/me/organizations', orgId) : Promise.resolve(null);
  // The org in the URL may simply not be this user's; that is the router
  // guard's call (organization-error page), not a reason to fail the load.
  const org = orgId ? apiGet(token, `/api/v1/organizations/${orgId}`, orgId).catch(() => null) : Promise.resolve(null);
  return {
    userId: claims.user ?? null,
    email: claims.email ?? null,
    result: Promise.all([me, orgs, org]).then(([me, orgs, org]) => ({ me, orgs, org })),
  };
}

// Firebase spends 600-800 ms restoring a persisted session (accounts:lookup)
// before it hands the user over. A returning visitor has the previous visit's
// token in the fm-secure-link cookie; when it is still valid and already
// carries the user claim, the startup requests go out with it right away.
// loadProfile() takes the result when Firebase confirms the same account and
// reruns the batch with Firebase's token on any failure; anything else is
// dropped.
let early = null;

// Returns the token's identity (for the feature flags client), or null.
export function startEarlyBootstrap(orgId = null) {
  const token = provisionalJwt();
  if (!token || !decodeClaims(token).user) return null;
  early = bootstrap(token, orgId);
  early.result.catch(() => {}); // consumed (or dropped) in loadProfile
  return { userId: early.userId, email: early.email };
}

export function takeEarlyBootstrap(userId) {
  const taken = early;
  early = null;
  return taken && userId && taken.userId === userId ? taken : null;
}
